import React, { useEffect, useState } from 'react';
import { Megaphone, Plus, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import PageHeader from './ui/PageHeader';
import EmptyState from './ui/EmptyState';
import apiService from '../services/apiService';

const emptyForm = {
  title: '',
  description: '',
  price: '',
  validUntil: '',
  departments: '',
  active: true,
};

export default function Campaigns({ isAdmin }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiService.fetchCampaigns();
      setCampaigns(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    setSaving(true);
    try {
      await apiService.createCampaign({
        title: form.title,
        description: form.description,
        price: form.price,
        validUntil: form.validUntil || null,
        departments: form.departments
          ? form.departments.split(',').map((d) => d.trim()).filter(Boolean)
          : [],
        active: form.active,
      });
      setForm(emptyForm);
      await load();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (campaign) => {
    if (!isAdmin) return;
    await apiService.updateCampaign(campaign.id, { active: !campaign.active });
    await load();
  };

  const handleDelete = async (id) => {
    if (!isAdmin || !window.confirm('Kampanya silinsin mi?')) return;
    await apiService.deleteCampaign(id);
    await load();
  };

  return (
    <div className="p-6 lg:p-8">
      <PageHeader
        title="Kampanyalar"
        subtitle="Facebook ve Instagram DM asistanının tanıtacağı sağlık paketleri"
        icon={Megaphone}
      />

      {isAdmin && (
        <form onSubmit={handleCreate} className="card mb-6 grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="field-label">Başlık</label>
            <input className="field" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          </div>
          <div className="md:col-span-2">
            <label className="field-label">Açıklama</label>
            <textarea className="field" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <label className="field-label">Fiyat</label>
            <input className="field" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
          </div>
          <div>
            <label className="field-label">Geçerlilik (yyyy-MM-dd)</label>
            <input className="field" type="date" value={form.validUntil} onChange={(e) => setForm({ ...form, validUntil: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <label className="field-label">Bölümler (virgülle)</label>
            <input className="field" value={form.departments} onChange={(e) => setForm({ ...form, departments: e.target.value })} placeholder="Check-Up, Dahiliye" />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
              <Plus className="h-4 w-4" /> Kampanya Ekle
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-slate-400">Yükleniyor…</p>
      ) : campaigns.length === 0 ? (
        <EmptyState title="Kampanya yok" description="Aktif kampanya ekleyin veya varsayılanları yükleyin." />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {campaigns.map((c) => (
            <div key={c.id} className="card">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-semibold text-white">{c.title}</h3>
                  <p className="mt-1 text-sm text-slate-400">{c.description}</p>
                </div>
                {isAdmin && (
                  <button type="button" onClick={() => toggleActive(c)} className="text-accent-400">
                    {c.active ? <ToggleRight className="h-6 w-6" /> : <ToggleLeft className="h-6 w-6 text-slate-500" />}
                  </button>
                )}
              </div>
              <p className="mt-3 text-sm text-accent-300">{c.price || 'Fiyat sorunuz'}</p>
              <p className="mt-1 text-xs text-slate-500">
                {(c.departments || []).join(', ') || 'Genel'}
                {c.validUntil ? ` · ${c.validUntil}` : ''}
              </p>
              {isAdmin && (
                <button type="button" onClick={() => handleDelete(c.id)} className="mt-4 text-sm text-rose-400 flex items-center gap-1">
                  <Trash2 className="h-4 w-4" /> Sil
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
